import { combineEpics } from "redux-observable";
import { of } from "rxjs";
import { catchError, filter, groupBy, map, mergeMap, switchMap } from "rxjs/operators";

import { actionCreators, isOfAction } from "../actions";
import { getFlattenRoomViews, getRoomSearchIsLoading, RoomType } from "../reducers/rooms";
import { getRoster } from "../reducers/roster";
import { notUndefined } from "../utils/any";
import { executeOperation } from "../utils/effects";
import { parse } from "../utils/query-string";
import { Epic } from "../utils/types";

const onSync: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.onSync)),
        mergeMap(() => {
            const { page, id: roomId, ts } = parse(window.location.search);
            if (
                page === "history" &&
                typeof roomId === "string" &&
                typeof ts === "string" &&
                parseInt(ts, 10) > 0
            ) {
                window.history.pushState(undefined, "", "/");
                return of(
                    actionCreators.setRoomToOpen({
                        roomId,
                        type: RoomType.HISTORY,
                        historyAroundTs: parseInt(ts, 10),
                    })
                );
            } else if (page === "chat" && typeof roomId === "string") {
                window.history.pushState(undefined, "", "/");
                return of(actionCreators.activateOrOpenRoom({ roomId }));
            }
            return of();
        })
    );
};

const onOpenAndSearch: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.openRoomSucceeded)),
        filter(
            ({
                payload: {
                    roomView: { type },
                },
            }) => type === RoomType.ROOM_SEARCH || type === RoomType.GLOBAL_SEARCH
        ),
        map(({ payload: { roomView } }) => {
            const { searchQuery } = roomView;
            if (searchQuery) {
                return actionCreators.roomSearchRequested({ roomView, searchQuery });
            }
            return undefined;
        }),
        filter(notUndefined)
    );
};

const onUpdateOpenRoom: Epic = action$ => {
    return action$.pipe(
        filter(isOfAction(actionCreators.roomSearchQueryUpdated)),
        filter(
            ({
                payload: {
                    roomView: { type },
                },
            }) => type === RoomType.ROOM_SEARCH || type === RoomType.GLOBAL_SEARCH
        ),
        mergeMap(({ payload: { roomView, searchQuery } }) => {
            if (searchQuery) {
                return of(actionCreators.roomSearchRequested({ roomView, searchQuery }));
            }
            return of();
        })
    );
};

const searchRoomHistory: Epic = (action$, state$, { App }) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.roomSearchRequested)),
        filter(
            ({
                payload: {
                    roomView: { type },
                },
            }) => type === RoomType.ROOM_SEARCH || type === RoomType.GLOBAL_SEARCH
        ),
        groupBy(
            ({
                payload: {
                    roomView: { viewId },
                },
            }) => viewId
        ),
        mergeMap(perRoomAction$ =>
            perRoomAction$.pipe(
                switchMap(({ payload: { roomView, searchQuery, cursor } }) => {
                    const { roomId } = roomView;
                    return executeOperation(() => {
                        if (roomView.type === RoomType.ROOM_SEARCH) {
                            return App.proto.search(
                                { room_id: roomId, match: searchQuery, cursor },
                                { tag: `[search] ${searchQuery}` }
                            );
                        } else {
                            // else if (roomView.type === RoomType.GLOBAL_SEARCH) {
                            return App.proto.search(
                                {
                                    match: searchQuery,
                                    cursor,
                                    sort: searchQuery.split(" ").length > 3 ? "score" : undefined,
                                },
                                { tag: `[global-search ${searchQuery}]` }
                            );
                        }
                    }).pipe(
                        mergeMap(({ result$ }) =>
                            result$.pipe(
                                mergeMap(data => {
                                    const { type } = roomView;
                                    if (type === RoomType.GLOBAL_SEARCH) {
                                        return [
                                            actionCreators.roomSearchSucceeded({
                                                roomView,
                                                data,
                                            }),
                                        ];
                                    } else if (type === RoomType.ROOM_SEARCH) {
                                        const { roomId } = roomView;
                                        const needLoading = !getRoster(state$.value).has(roomId);
                                        const loadAction = actionCreators.ensureRoomsLoadedRequested(
                                            {
                                                roomIds: [roomId],
                                                tag: "[gs_active_search_view]",
                                            }
                                        );
                                        const succeededAction = actionCreators.roomSearchSucceeded({
                                            roomView,
                                            data,
                                        });

                                        if (needLoading) {
                                            return [succeededAction, loadAction];
                                        } else {
                                            return [succeededAction];
                                        }
                                    } else {
                                        return of<never>();
                                    }
                                }),
                                catchError((err, retry) =>
                                    of(actionCreators.roomSearchFailed({ roomView, err })).pipe(
                                        mergeMap(() => {
                                            if (
                                                getFlattenRoomViews(state$.value).includes(
                                                    roomView
                                                ) &&
                                                getRoomSearchIsLoading(state$.value, roomView)
                                            ) {
                                                return retry;
                                            }
                                            return of<never>();
                                        })
                                    )
                                )
                            )
                        )
                    );
                })
            )
        )
    );
};

const onSearchResults: Epic = action$ => {
    return action$.pipe(
        filter(isOfAction(actionCreators.roomSearchSucceeded)),
        filter(
            ({
                payload: {
                    roomView: { type },
                },
            }) => type === RoomType.GLOBAL_SEARCH
        ),
        map(({ payload: { data: { page } } }) => page.map(r => r.room_id).filter(notUndefined)),
        map((roomIds: string[]) => {
            if (roomIds) {
                return actionCreators.ensureRoomsLoadedRequested({
                    roomIds,
                    tag: "[gs_room_ids]",
                });
            } else {
                return undefined;
            }
        }),
        filter(notUndefined)
    );
};

export const roomsSearchEpic = combineEpics(
    onSync,
    onOpenAndSearch,
    onUpdateOpenRoom,
    searchRoomHistory,
    onSearchResults
);
